import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import cytoscape from 'cytoscape';
import { useAppContext } from '../../context/AppContext';
import { Network, FileText, Lightbulb, MessageSquare, Tag, Filter, ZoomIn, ZoomOut, Maximize2, Download } from 'lucide-react';

interface KnowledgeNode {
  id: string;
  type: 'component' | 'email' | 'fact' | 'narrative';
  label: string;
  category?: string;
  metadata?: any;
}

interface KnowledgeEdge {
  id: string;
  source: string;
  target: string;
  type: string;
  relationship?: string;
  strength?: number;
  metadata?: any;
}

interface KnowledgeGraphData {
  nodes: KnowledgeNode[];
  edges: KnowledgeEdge[];
  facts: any[];
  narratives: any[];
}

export const KnowledgeMapTab: React.FC = () => {
  const { state } = useAppContext();
  const selectedComponents = state.selectedComponents;
  const containerRef = useRef<HTMLDivElement>(null);
  const cyRef = useRef<cytoscape.Core | null>(null);
  
  const [graphData, setGraphData] = useState<KnowledgeGraphData>({
    nodes: [],
    edges: [],
    facts: [],
    narratives: []
  });
  
  const [selectedNode, setSelectedNode] = useState<any>(null);
  const [filterOptions, setFilterOptions] = useState({
    showComponents: true,
    showEmails: true,
    showFacts: true,
    showNarratives: true,
    showOnlySelected: false
  });
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    components: 0,
    emails: 0,
    facts: 0,
    narratives: 0,
    edges: 0
  });

  // Load all data files
  useEffect(() => {
    const loadKnowledgeData = async () => {
      try {
        setLoading(true);
        
        // Try to load the generated data files
        const [networkRes, emailsRes, knowledgeRes] = await Promise.all([
          fetch('/mock_data/solar_farm_comprehensive_data_enhanced_network_2025-09-02_14-07-18.json'),
          fetch('/mock_data/solar_farm_comprehensive_data_emails_2025-09-02_14-07-18.json'),
          fetch('/mock_data/solar_farm_comprehensive_data_knowledge_graph_2025-09-02_14-07-18.json')
        ]);

        let networkData = null;
        let emailsData = null;
        let knowledgeData = null;

        if (networkRes.ok) {
          networkData = await networkRes.json();
        }
        if (emailsRes.ok) {
          emailsData = await emailsRes.json();
        }
        if (knowledgeRes.ok) {
          knowledgeData = await knowledgeRes.json();
        }

        // If no data files, generate sample data
        if (!networkData && !emailsData && !knowledgeData) {
          generateSampleKnowledgeGraph();
        } else {
          processKnowledgeData(networkData, emailsData, knowledgeData);
        }
      } catch (error) {
        console.error('Error loading knowledge data:', error);
        generateSampleKnowledgeGraph();
      } finally {
        setLoading(false);
      }
    };

    loadKnowledgeData();
  }, []);

  // Process loaded data into graph format
  const processKnowledgeData = (networkData: any, emailsData: any, knowledgeData: any) => {
    const nodes: KnowledgeNode[] = [];
    const edges: KnowledgeEdge[] = [];
    const nodeMap = new Map<string, boolean>();

    // Add component nodes (limit to reduce complexity)
    if (networkData?.nodes) {
      const componentNodes = networkData.nodes.slice(0, 50); // Limit to first 50 components for performance
      componentNodes.forEach((node: any) => {
        if (!nodeMap.has(node.id)) {
          nodes.push({
            id: node.id,
            type: 'component',
            label: node.label || node.id,
            category: node.node_type,
            metadata: node.metadata
          });
          nodeMap.set(node.id, true);
        }
      });
    }

    // Add email nodes (limit to recent/relevant)
    if (emailsData) {
      const emailNodes = emailsData.slice(-30); // Last 30 emails
      emailNodes.forEach((email: any) => {
        if (!nodeMap.has(email.id)) {
          nodes.push({
            id: email.id,
            type: 'email',
            label: email.subject.substring(0, 50) + '...',
            category: email.category,
            metadata: email
          });
          nodeMap.set(email.id, true);
        }
      });
    }

    // Add fact nodes
    if (knowledgeData?.fact_nodes) {
      knowledgeData.fact_nodes.forEach((fact: any) => {
        if (!nodeMap.has(fact.id)) {
          nodes.push({
            id: fact.id,
            type: 'fact',
            label: fact.content.substring(0, 50) + '...',
            category: fact.category,
            metadata: fact
          });
          nodeMap.set(fact.id, true);
        }
      });
    }

    // Add narrative nodes
    if (knowledgeData?.narrative_nodes) {
      knowledgeData.narrative_nodes.forEach((narrative: any) => {
        if (!nodeMap.has(narrative.id)) {
          nodes.push({
            id: narrative.id,
            type: 'narrative',
            label: narrative.content.substring(0, 50) + '...',
            category: narrative.category,
            metadata: narrative
          });
          nodeMap.set(narrative.id, true);
        }
      });
    }

    // Add knowledge edges (only between nodes that exist)
    if (knowledgeData?.knowledge_edges) {
      knowledgeData.knowledge_edges.forEach((edge: any) => {
        if (nodeMap.has(edge.source) && nodeMap.has(edge.target)) {
          edges.push({
            id: edge.id,
            source: edge.source,
            target: edge.target,
            type: edge.type,
            relationship: edge.relationship,
            strength: edge.strength,
            metadata: edge.metadata
          });
        }
      });
    }

    setGraphData({
      nodes,
      edges,
      facts: knowledgeData?.fact_nodes || [],
      narratives: knowledgeData?.narrative_nodes || []
    });

    // Update stats
    setStats({
      components: nodes.filter(n => n.type === 'component').length,
      emails: nodes.filter(n => n.type === 'email').length,
      facts: nodes.filter(n => n.type === 'fact').length,
      narratives: nodes.filter(n => n.type === 'narrative').length,
      edges: edges.length
    });
  };

  // Generate sample knowledge graph if no data files
  const generateSampleKnowledgeGraph = () => {
    const nodes: KnowledgeNode[] = [];
    const edges: KnowledgeEdge[] = [];

    // Sample component nodes
    ['INV-1.1', 'MV-1', '1.1.1.C.3'].forEach(id => {
      nodes.push({
        id,
        type: 'component',
        label: id,
        category: id.startsWith('INV') ? 'inverter' : id.startsWith('MV') ? 'transformer' : 'combiner'
      });
    });

    // Sample email nodes
    for (let i = 1; i <= 5; i++) {
      nodes.push({
        id: `EMAIL_${i}`,
        type: 'email',
        label: `Email about maintenance #${i}`,
        category: 'maintenance'
      });
    }

    // Sample fact nodes
    nodes.push(
      {
        id: 'FACT_1',
        type: 'fact',
        label: 'Dust reduces efficiency by 2% monthly',
        category: 'dust_removal'
      },
      {
        id: 'FACT_2',
        type: 'fact',
        label: 'Firmware updates improve MPPT by 5%',
        category: 'component_update'
      }
    );

    // Sample narrative nodes
    nodes.push({
      id: 'NARRATIVE_1',
      type: 'narrative',
      label: 'Field team noticed unusual wear patterns',
      category: 'maintenance'
    });

    // Sample edges
    edges.push(
      { id: 'E1', source: 'EMAIL_1', target: 'INV-1.1', type: 'references' },
      { id: 'E2', source: 'EMAIL_1', target: 'FACT_1', type: 'supports' },
      { id: 'E3', source: 'FACT_2', target: 'INV-1.1', type: 'applies_to' },
      { id: 'E4', source: 'NARRATIVE_1', target: '1.1.1.C.3', type: 'relates_to' }
    );

    setGraphData({ nodes, edges, facts: [], narratives: [] });
    setStats({
      components: 3,
      emails: 5,
      facts: 2,
      narratives: 1,
      edges: 4
    });
  };

  // Initialize Cytoscape
  useEffect(() => {
    if (!containerRef.current || graphData.nodes.length === 0) return;

    // Filter nodes and edges based on options
    const filteredNodes = graphData.nodes.filter(node => {
      if (!filterOptions.showComponents && node.type === 'component') return false;
      if (!filterOptions.showEmails && node.type === 'email') return false;
      if (!filterOptions.showFacts && node.type === 'fact') return false;
      if (!filterOptions.showNarratives && node.type === 'narrative') return false;
      
      if (filterOptions.showOnlySelected && selectedComponents.length > 0) {
        if (node.type === 'component' && !selectedComponents.includes(node.id)) return false;
      }
      
      return true;
    });

    const nodeIds = new Set(filteredNodes.map(n => n.id));
    const filteredEdges = graphData.edges.filter(edge => 
      nodeIds.has(edge.source) && nodeIds.has(edge.target)
    );

    // Prepare cytoscape elements
    const elements = [
      ...filteredNodes.map(node => ({
        data: {
          id: node.id,
          label: node.label,
          type: node.type,
          category: node.category,
          ...node.metadata
        },
        classes: `${node.type} ${node.category || ''}`
      })),
      ...filteredEdges.map(edge => ({
        data: {
          id: edge.id,
          source: edge.source,
          target: edge.target,
          type: edge.type,
          relationship: edge.relationship,
          strength: edge.strength || 0.5
        },
        classes: edge.type
      }))
    ];

    // Initialize Cytoscape
    cyRef.current = cytoscape({
      container: containerRef.current,
      elements,
      style: [
        // Node styles
        {
          selector: 'node',
          style: {
            'label': 'data(label)',
            'text-valign': 'center',
            'text-halign': 'center',
            'text-wrap': 'wrap',
            'text-max-width': '100px',
            'font-size': '10px',
            'background-color': '#6B7280',
            'border-width': 2,
            'border-color': '#374151',
            'width': 40,
            'height': 40,
            'color': '#E5E7EB'
          }
        },
        // Component nodes
        {
          selector: '.component',
          style: {
            'background-color': '#3B82F6',
            'border-color': '#1E40AF',
            'shape': 'round-rectangle'
          }
        },
        // Email nodes
        {
          selector: '.email',
          style: {
            'background-color': '#10B981',
            'border-color': '#047857',
            'shape': 'round-tag'
          }
        },
        // Fact nodes
        {
          selector: '.fact',
          style: {
            'background-color': '#F59E0B',
            'border-color': '#D97706',
            'shape': 'round-diamond'
          }
        },
        // Narrative nodes
        {
          selector: '.narrative',
          style: {
            'background-color': '#8B5CF6',
            'border-color': '#6D28D9',
            'shape': 'round-octagon'
          }
        },
        // Edge styles
        {
          selector: 'edge',
          style: {
            'width': 'mapData(strength, 0, 1, 1, 4)',
            'line-color': '#4B5563',
            'target-arrow-color': '#4B5563',
            'target-arrow-shape': 'triangle',
            'curve-style': 'bezier',
            'opacity': 0.7,
            'label': 'data(type)',
            'font-size': '8px',
            'text-rotation': 'autorotate',
            'text-margin-y': -10,
            'color': '#9CA3AF'
          }
        },
        // Edge type styles
        {
          selector: '.references',
          style: {
            'line-color': '#3B82F6',
            'target-arrow-color': '#3B82F6'
          }
        },
        {
          selector: '.supports',
          style: {
            'line-color': '#10B981',
            'target-arrow-color': '#10B981'
          }
        },
        {
          selector: '.applies_to',
          style: {
            'line-color': '#F59E0B',
            'target-arrow-color': '#F59E0B'
          }
        },
        {
          selector: '.relates_to',
          style: {
            'line-color': '#8B5CF6',
            'target-arrow-color': '#8B5CF6',
            'line-style': 'dashed'
          }
        },
        // Selected state
        {
          selector: ':selected',
          style: {
            'background-color': '#ef5350',
            'border-color': '#DC2626',
            'border-width': 4,
            'z-index': 999
          }
        }
      ],
      layout: {
        name: 'cose',
        animate: true,
        animationDuration: 1000,
        nodeRepulsion: 400000,
        idealEdgeLength: 100,
        edgeElasticity: 100,
        nestingFactor: 5,
        gravity: 80,
        numIter: 1000,
        initialTemp: 200,
        coolingFactor: 0.95,
        minTemp: 1.0
      },
      minZoom: 0.1,
      maxZoom: 5,
      wheelSensitivity: 0.2
    });

    // Add event listeners
    cyRef.current.on('tap', 'node', (evt) => {
      const node = evt.target;
      setSelectedNode({
        id: node.id(),
        type: node.data('type'),
        label: node.data('label'),
        category: node.data('category'),
        metadata: node.data()
      });
    });

    cyRef.current.on('tap', (evt) => {
      if (evt.target === cyRef.current) {
        setSelectedNode(null);
      }
    });

    // Cleanup
    return () => {
      cyRef.current?.destroy();
    };
  }, [graphData, filterOptions, selectedComponents]);

  // Graph controls
  const handleZoomIn = () => {
    cyRef.current?.zoom(cyRef.current.zoom() * 1.2);
    cyRef.current?.center();
  };

  const handleZoomOut = () => {
    cyRef.current?.zoom(cyRef.current.zoom() * 0.8);
    cyRef.current?.center();
  };

  const handleFit = () => {
    cyRef.current?.fit();
  };

  const handleExport = () => {
    if (!cyRef.current) return;
    
    const exportData = {
      nodes: graphData.nodes,
      edges: graphData.edges,
      timestamp: new Date().toISOString(),
      filters: filterOptions
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `knowledge_map_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Node details panel
  const NodeDetailsPanel = () => {
    if (!selectedNode) return null;

    const getNodeIcon = (type: string) => {
      switch (type) {
        case 'component': return <Network className="w-5 h-5" />;
        case 'email': return <MessageSquare className="w-5 h-5" />;
        case 'fact': return <Lightbulb className="w-5 h-5" />;
        case 'narrative': return <FileText className="w-5 h-5" />;
        default: return <Tag className="w-5 h-5" />;
      }
    };

    const getNodeColor = (type: string) => {
      switch (type) {
        case 'component': return 'text-blue-400';
        case 'email': return 'text-green-400';
        case 'fact': return 'text-yellow-400';
        case 'narrative': return 'text-purple-400';
        default: return 'text-gray-400';
      }
    };

    return (
      <div className="absolute right-4 top-20 w-80 bg-gray-800 rounded-lg border border-gray-700 shadow-xl overflow-hidden">
        <div className="p-4 border-b border-gray-700">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <span className={getNodeColor(selectedNode.type)}>
                {getNodeIcon(selectedNode.type)}
              </span>
              <div>
                <h3 className="font-medium text-gray-100">
                  {selectedNode.type.charAt(0).toUpperCase() + selectedNode.type.slice(1)}
                </h3>
                <p className="text-xs text-gray-400">{selectedNode.id}</p>
              </div>
            </div>
            <button
              onClick={() => setSelectedNode(null)}
              className="text-gray-400 hover:text-gray-200"
            >
              ×
            </button>
          </div>
        </div>
        
        <div className="p-4 max-h-96 overflow-y-auto">
          <div className="space-y-3">
            <div>
              <p className="text-xs text-gray-400 mb-1">Label</p>
              <p className="text-sm text-gray-200">{selectedNode.label}</p>
            </div>
            
            {selectedNode.category && (
              <div>
                <p className="text-xs text-gray-400 mb-1">Category</p>
                <p className="text-sm text-gray-200">
                  {selectedNode.category.replace('_', ' ')}
                </p>
              </div>
            )}

            {selectedNode.metadata && (
              <div>
                <p className="text-xs text-gray-400 mb-1">Details</p>
                <div className="bg-gray-900 rounded p-2">
                  <pre className="text-xs text-gray-300 whitespace-pre-wrap">
                    {JSON.stringify(selectedNode.metadata, null, 2).substring(0, 500)}
                  </pre>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-900">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-900">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 p-4">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-xl font-bold text-gray-100">Knowledge Map</h1>
          <div className="flex items-center gap-4 text-sm text-gray-400">
            <span>Components: {stats.components}</span>
            <span>Emails: {stats.emails}</span>
            <span>Facts: {stats.facts}</span>
            <span>Narratives: {stats.narratives}</span>
            <span>Connections: {stats.edges}</span>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-400">Show:</span>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filterOptions.showComponents}
              onChange={(e) => setFilterOptions(prev => ({ ...prev, showComponents: e.target.checked }))}
              className="rounded border-gray-600 bg-gray-700 text-blue-500"
            />
            <span className="text-sm text-gray-200">Components</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filterOptions.showEmails}
              onChange={(e) => setFilterOptions(prev => ({ ...prev, showEmails: e.target.checked }))}
              className="rounded border-gray-600 bg-gray-700 text-green-500"
            />
            <span className="text-sm text-gray-200">Emails</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filterOptions.showFacts}
              onChange={(e) => setFilterOptions(prev => ({ ...prev, showFacts: e.target.checked }))}
              className="rounded border-gray-600 bg-gray-700 text-yellow-500"
            />
            <span className="text-sm text-gray-200">Facts</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filterOptions.showNarratives}
              onChange={(e) => setFilterOptions(prev => ({ ...prev, showNarratives: e.target.checked }))}
              className="rounded border-gray-600 bg-gray-700 text-purple-500"
            />
            <span className="text-sm text-gray-200">Narratives</span>
          </label>

          {selectedComponents.length > 0 && (
            <label className="flex items-center gap-2 cursor-pointer ml-4">
              <input
                type="checkbox"
                checked={filterOptions.showOnlySelected}
                onChange={(e) => setFilterOptions(prev => ({ ...prev, showOnlySelected: e.target.checked }))}
                className="rounded border-gray-600 bg-gray-700 text-accent-red"
              />
              <span className="text-sm text-gray-200">Selected Components Only</span>
            </label>
          )}
        </div>
      </div>

      {/* Graph container */}
      <div className="flex-1 relative">
        <div ref={containerRef} className="w-full h-full" />

        {/* Controls */}
        <div className="absolute left-4 top-4 flex flex-col gap-2">
          <button
            onClick={handleZoomIn}
            className="p-2 bg-gray-800 hover:bg-gray-700 rounded border border-gray-700"
            title="Zoom In"
          >
            <ZoomIn className="w-4 h-4 text-gray-400" />
          </button>
          <button
            onClick={handleZoomOut}
            className="p-2 bg-gray-800 hover:bg-gray-700 rounded border border-gray-700"
            title="Zoom Out"
          >
            <ZoomOut className="w-4 h-4 text-gray-400" />
          </button>
          <button
            onClick={handleFit}
            className="p-2 bg-gray-800 hover:bg-gray-700 rounded border border-gray-700"
            title="Fit to Screen"
          >
            <Maximize2 className="w-4 h-4 text-gray-400" />
          </button>
          <button
            onClick={handleExport}
            className="p-2 bg-gray-800 hover:bg-gray-700 rounded border border-gray-700"
            title="Export Graph"
          >
            <Download className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Legend */}
        <div className="absolute left-4 bottom-4 bg-gray-800 rounded-lg border border-gray-700 p-3">
          <h3 className="text-xs font-medium text-gray-400 mb-2">Legend</h3>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-500 rounded" />
              <span className="text-xs text-gray-300">Component</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded" />
              <span className="text-xs text-gray-300">Email</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-yellow-500 rounded" />
              <span className="text-xs text-gray-300">Fact</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-purple-500 rounded" />
              <span className="text-xs text-gray-300">Narrative</span>
            </div>
          </div>
        </div>

        {/* Node details panel */}
        <NodeDetailsPanel />
      </div>
    </div>
  );
};